import { useEffect, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import Sidebar from '../components/layout/sidebar/sidebar'
import { MainView } from '../components/layout/main'
import {
    FundableClientsStat,
    ClientsWithClaimsStat,
    AdminReportsSection,
    PayableClaimsProgress,
    SectionVisibilityGroup,
    PcpReferralClientSearch,
    IntroducerClientActions,
} from '../components/reusable'
import type { AdminReportOption } from '../components/reusable'
import api from '../helpers/apiRequester'
import { getLoginUrl } from '../helpers/authConfig'
import { StatsView } from '../components/views'
import Modal from '../components/reusable/modal'
import { ViewChartDataModalContent } from '../components/modalContent/viewChartDataContent'
import { Page, PageHeader, Section, Button, Divider } from '../ui'

const CAMPAIGN_LABEL = '21 Jun 2026'

// Admin-only eligibility exports (Sentinel users only — the section hides itself
// for everyone else, and each reportType is independently gated server-side).
// These are scoped to the current campaign on the backend so they reconcile with
// the "Fundable Clients (Current Campaign)" stat.
const PCP_REFERRAL_REPORTS: AdminReportOption[] = [
    { value: 'pcp-referral-fundable-clients', label: 'Clients with Claims (Fundable)', filename: 'fundable-clients' },
    { value: 'pcp-referral-signed-clients', label: 'All Clients Eligibility', filename: 'all-clients-eligibility' },
    { value: 'pcp-referral-claims-eligibility', label: 'Claims Eligibility Breakdown', filename: 'claims-eligibility' },
]

function PcpReferralOverview() {
    const [dataReady, setDataReady] = useState(false)
    const [loading, setLoading] = useState(false)
    const [overviewData, setOverviewData] = useState<any>()
    const [showHistorical, setShowHistorical] = useState(false)
    const [modalChartData, setModalChartData] = useState<any>()
    const [modalState, setModalState] = useState(false)
    const [modalTitle, setModalTitle] = useState('')
    const [modalDate, setModalDate] = useState('')

    useEffect(() => {
        if (modalChartData != null) {
            setModalState(true)
        }
    }, [modalChartData])

    useEffect(() => {
        if (!dataReady && !loading) {
            setLoading(true)
            getData()
        }
    }, [dataReady, loading])

    const getData = async () => {
        Loading.circle('Loading...')
        try {
            const apiResponse = await api({ url: '/pcp-referral-leads' })
            const data = apiResponse.data
            setOverviewData(data)
            setDataReady(true)
            Loading.remove()
        } catch (error: any) {
            console.log(error)
            Loading.remove()
            if (error?.response?.status == 401) {
                Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                    window.location.href = getLoginUrl()
                })
            } else {
                window.location.href = getLoginUrl()
            }
        }
    }

    const prepChartData = (data: any) => {
        setModalChartData(data.dataResults)
        setModalTitle(data.title)
        setModalDate(data.date)
    }

    const toggleModal = () => {
        setModalChartData(null)
        setModalState(false)
    }

    const historical = overviewData?.historical

    return (
        <div className="home">
            <Sidebar />
            <MainView>
                <Page>
                    <PageHeader eyebrow="PCP Referral" title={'PCP Referral Lead Tracker'} />
                    <div>
                        {dataReady ? (
                            <>
                                <Section title="Current Campaign" />
                                <SectionVisibilityGroup audience="introducer" introducer="PCP Referral">
                                    <ClientsWithClaimsStat
                                        title="Clients with Claims (Current Campaign)"
                                        count={overviewData?.clientsWithClaims?.current}
                                        subtitle="Clients across all teams with at least one policy-bearing claim (this campaign). Download the full list — client name, signed date/time, team and signing agent."
                                        requestBody={{ reportType: 'pcp-referral-clients-with-claims', isFullReport: true }}
                                        filenamePrefix="pcp-referral-clients-with-claims"
                                        secondaryDownload={{
                                            label: 'Download eligibility report',
                                            requestBody: {
                                                reportType: 'pcp-referral-all-clients-eligibility',
                                                isFullReport: true,
                                            },
                                            filenamePrefix: 'pcp-referral-all-clients-eligibility',
                                        }}
                                    />
                                    <StatsView
                                        stats={overviewData.stats}
                                        chartData={overviewData?.chartData}
                                        dataGroup={overviewData.group}
                                        handleDataModal={prepChartData}
                                        fundableIntroducer="pcpReferral"
                                        fundableCohort="current"
                                        onlyTitles={['PCP Referral Leads Progress']}
                                    />
                                    <PayableClaimsProgress introducer="pcpReferral" cohort="current" />
                                    <StatsView
                                        stats={overviewData.stats}
                                        chartData={overviewData?.chartData}
                                        dataGroup={overviewData.group}
                                        handleDataModal={prepChartData}
                                        onlyTitles={['Credit Reporting', 'Errors']}
                                    />
                                </SectionVisibilityGroup>
                                <SectionVisibilityGroup audience="admin" introducer="PCP Referral">
                                    <FundableClientsStat
                                        introducer="pcpReferral"
                                        cohort="current"
                                        title="Fundable Clients (Current Campaign)"
                                    />
                                    <AdminReportsSection
                                        reportOptions={PCP_REFERRAL_REPORTS}
                                        filenamePrefix="pcp-referral"
                                        subtitle="Eligibility exports for the current campaign — reconcile with the Fundable Clients figure above."
                                    />
                                    <StatsView
                                        stats={overviewData.stats}
                                        chartData={overviewData?.chartData}
                                        dataGroup={overviewData.group}
                                        handleDataModal={prepChartData}
                                        onlyTitles={['Claims Progress']}
                                    />
                                    <PcpReferralClientSearch />
                                    <IntroducerClientActions introducer="pcpReferral" introducerLabel="PCP Referral" />
                                </SectionVisibilityGroup>

                                {historical && (
                                    <div>
                                        <Divider />
                                        <Section
                                            title="Admin — Historical"
                                            description={`Activity before ${CAMPAIGN_LABEL}.`}
                                            actions={
                                                <Button
                                                    variant="neutral"
                                                    size="sm"
                                                    onClick={() => setShowHistorical((v) => !v)}
                                                >
                                                    {showHistorical ? 'Hide historical' : 'Show historical'}
                                                </Button>
                                            }
                                        />
                                        {showHistorical && (
                                            <>
                                                <FundableClientsStat
                                                    introducer="pcpReferral"
                                                    cohort="historical"
                                                    title="Fundable Clients (Historical)"
                                                />
                                                <ClientsWithClaimsStat
                                                    title="Clients with Claims (Historical)"
                                                    count={overviewData?.clientsWithClaims?.historical}
                                                    subtitle="Pre-campaign clients with at least one policy-bearing claim (admin only). Download the full list — client name, signed date/time, team and signing agent."
                                                    requestBody={{
                                                        reportType: 'pcp-referral-clients-with-claims-historical',
                                                        isFullReport: true,
                                                    }}
                                                    filenamePrefix="pcp-referral-clients-with-claims-historical"
                                                />
                                                <StatsView
                                                    stats={historical.stats}
                                                    chartData={historical.chartData}
                                                    dataGroup={historical.group || 'pcpReferral'}
                                                    handleDataModal={prepChartData}
                                                    fundableIntroducer="pcpReferral"
                                                    fundableCohort="historical"
                                                />
                                                <PayableClaimsProgress introducer="pcpReferral" cohort="historical" />
                                            </>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                </Page>
            </MainView>
            <Modal modalState={modalState} previewModalAction={toggleModal}>
                <ViewChartDataModalContent
                    chartData={modalChartData}
                    closeModal={toggleModal}
                    chartTitle={modalTitle}
                    chartDate={modalDate}
                />
            </Modal>
        </div>
    )
}

export default PcpReferralOverview
